import type { Contract } from '../types';

export interface AgreementHistoryNode<T extends { contract: Contract }> {
  group: T;
  amendments: T[];
}

/** Nest each ÄTA under the base contract it amends. The owner is found by
 * following the stored stageVariation links, never by list position, so an
 * ÄTA of an ÄTA still lands under its original contract. */
export function agreementHistoryTree<T extends { contract: Contract }>(groups: T[]): AgreementHistoryNode<T>[] {
  const byId = new Map(groups.map(group => [group.contract.id, group]));
  const ownerOf = (group: T): T | undefined => {
    const seen = new Set<string>();
    let current = group;
    while (current.contract.stageVariation && !seen.has(current.contract.id)) {
      seen.add(current.contract.id);
      const parent = byId.get(current.contract.stageVariation.originalContractId);
      if (!parent) return undefined;
      current = parent;
    }
    return current.contract.stageVariation ? undefined : current;
  };
  const byNumber = (a: T, b: T) => a.contract.contractNumber - b.contract.contractNumber;
  const nodes = new Map<string, AgreementHistoryNode<T>>();
  const orphans: T[] = [];
  groups.filter(group => !group.contract.stageVariation)
    .forEach(group => nodes.set(group.contract.id, { group, amendments: [] }));
  groups.filter(group => group.contract.stageVariation).forEach(group => {
    const owner = ownerOf(group);
    const node = owner && nodes.get(owner.contract.id);
    if (node) node.amendments.push(group);
    else orphans.push(group);
  });
  return [...nodes.values(), ...orphans.map(group => ({ group, amendments: [] }))]
    .sort((a, b) => byNumber(a.group, b.group))
    .map(node => ({ ...node, amendments: node.amendments.sort(byNumber) }));
}
